import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";

const getMappedItems = (jobResult) =>
  jobResult?.mappedItems?.edges?.map((edge) => edge.node) || [];

const getUnansweredQuestions = (jobResult) =>
  jobResult?.unansweredQuestions || [];

export const QuestionMappingForm = ({
  assignment,
  jobResult,
  sourcePageCount,
  onComplete,
  onSubmittingChange,
}) => {
  const ApiClient = useApiClient();
  const mappedItems = useMemo(() => getMappedItems(jobResult), [jobResult]);
  const unansweredQuestions = useMemo(
    () => getUnansweredQuestions(jobResult),
    [jobResult],
  );
  const mappedByQuestionId = useMemo(
    () =>
      new Map(mappedItems.map((item) => [item.assignmentQuestion?.id, item])),
    [mappedItems],
  );
  const unansweredByQuestionId = useMemo(
    () =>
      new Map(
        unansweredQuestions.map((item) => [item.assignmentQuestion?.id, item]),
      ),
    [unansweredQuestions],
  );
  const initialValues = useMemo(
    () =>
      Object.fromEntries(
        (assignment?.assignmentQuestions || []).map((question) => {
          const mappedItem = mappedByQuestionId.get(question.id);
          const unansweredItem = unansweredByQuestionId.get(question.id);
          return [
            question.id,
            {
              pages: mappedItem?.pages || unansweredItem?.pages || [],
              reviewState: mappedItem
                ? "answered"
                : unansweredItem?.isConfirmed
                  ? "unanswered"
                  : unansweredItem
                    ? "detected"
                    : "unresolved",
            },
          ];
        }),
      ),
    [assignment, mappedByQuestionId, unansweredByQuestionId],
  );
  const [questionValues, setQuestionValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setQuestionValues(initialValues);
  }, [initialValues]);
  useEffect(
    () => onSubmittingChange?.(submitting),
    [onSubmittingChange, submitting],
  );

  const availablePages = useMemo(
    () => Array.from({ length: sourcePageCount || 0 }, (_, index) => index + 1),
    [sourcePageCount],
  );

  const overlappingPages = useMemo(() => {
    const pageOwners = new Map();
    Object.entries(questionValues).forEach(([questionId, value]) => {
      value.pages.forEach((page) => {
        pageOwners.set(page, [...(pageOwners.get(page) || []), questionId]);
      });
    });
    return new Set(
      [...pageOwners.entries()]
        .filter(([, owners]) => owners.length > 1)
        .map(([page]) => page),
    );
  }, [questionValues]);

  const handleSubmit = useCallback(async () => {
    const questions = assignment?.assignmentQuestions || [];
    const changedQuestions = questions.filter(
      (question) =>
        JSON.stringify(questionValues[question.id]) !==
        JSON.stringify(initialValues[question.id]),
    );
    if (!changedQuestions.length) {
      enqueueSnackbar("No mapping changes to save.", { variant: "info" });
      return;
    }

    const invalidQuestion = changedQuestions.find((question) => {
      const value = questionValues[question.id];
      return value.reviewState !== "unanswered" && value.pages.length === 0;
    });
    if (invalidQuestion) {
      enqueueSnackbar(
        "Select at least one page or confirm that the student did not answer this question.",
        { variant: "error" },
      );
      return;
    }

    if (
      changedQuestions.some(
        (question) => questionValues[question.id].reviewState === "detected",
      )
    ) {
      enqueueSnackbar(
        "Confirm that the student did not answer this question or use the selected pages as an answer.",
        { variant: "error" },
      );
      return;
    }

    setSubmitting(true);
    try {
      const response = await ApiClient.assignment.reviewQuestionPageMappings({
        fileProcessingResult: jobResult.id,
        mappings: changedQuestions.map((question) => ({
          assignmentQuestion: question.id,
          pages: questionValues[question.id].pages,
          studentDidNotAnswer:
            questionValues[question.id].reviewState === "unanswered",
        })),
      });
      if (!response?.success) {
        throw new Error(response?.message || "Could not update mappings");
      }
      enqueueSnackbar(response.message || "Mappings updated", {
        variant: "success",
      });
      await onComplete?.();
    } catch (error) {
      enqueueSnackbar(error.message || "Could not update mappings", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    ApiClient.assignment,
    assignment,
    initialValues,
    jobResult.id,
    onComplete,
    questionValues,
  ]);

  return (
    <Stack
      id="question-page-mapping-form"
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
      spacing={2}
    >
      <Typography variant="body2" color="text.secondary">
        Review the source document pages assigned to each question. If no answer
        was provided, confirm that the student did not answer this question.
      </Typography>
      {(assignment?.assignmentQuestions || []).map((question) => {
        const mappedItem = mappedByQuestionId.get(question.id);
        const value = questionValues[question.id] || {
          pages: [],
          reviewState: "unresolved",
        };
        const selectedPages = value.pages;
        const overlaps = selectedPages.filter((page) =>
          overlappingPages.has(page),
        );

        return (
          <Card key={question.id} variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">
                    Q{question.identifier || question.order + 1}
                  </Typography>
                  <Chip
                    size="small"
                    color={
                      value.reviewState === "unanswered"
                        ? "info"
                        : value.reviewState === "detected"
                          ? "warning"
                          : mappedItem
                            ? "success"
                            : "warning"
                    }
                    label={
                      value.reviewState === "unanswered"
                        ? "Student did not answer this question"
                        : value.reviewState === "detected"
                          ? "No answer text was detected"
                          : mappedItem
                            ? "Mapped"
                            : "Unmapped"
                    }
                  />
                  {mappedItem?.autoMapped &&
                    value.reviewState === "answered" && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Auto-mapped"
                      />
                    )}
                </Stack>
                <Typography variant="body2">
                  {question.question?.questionText}
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id={`page-select-label-${question.id}`}>
                    Source document pages
                  </InputLabel>
                  <Select
                    multiple
                    labelId={`page-select-label-${question.id}`}
                    label="Source document pages"
                    value={selectedPages}
                    onChange={(event) =>
                      setQuestionValues((current) => ({
                        ...current,
                        [question.id]: {
                          ...current[question.id],
                          pages: event.target.value.sort((a, b) => a - b),
                          reviewState:
                            current[question.id].reviewState === "detected"
                              ? "detected"
                              : "answered",
                        },
                      }))
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((page) => (
                          <Chip
                            key={page}
                            label={`Page ${page}`}
                            size="small"
                            sx={{
                              backgroundColor: "background.paper",
                              color: "text.primary",
                              "& .MuiChip-label": { color: "text.primary" },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {availablePages.map((page) => (
                      <MenuItem
                        key={page}
                        value={page}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "action.selected",
                            color: "text.primary",
                            "& .MuiListItemText-primary": {
                              color: "text.primary",
                            },
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "action.hover",
                            color: "text.primary",
                            "& .MuiListItemText-primary": {
                              color: "text.primary",
                            },
                          },
                        }}
                      >
                        <Checkbox checked={selectedPages.includes(page)} />
                        <ListItemText primary={`Page ${page}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={value.reviewState === "unanswered"}
                      onChange={(event) =>
                        setQuestionValues((current) => ({
                          ...current,
                          [question.id]: {
                            ...current[question.id],
                            reviewState: event.target.checked
                              ? "unanswered"
                              : current[question.id].pages.length
                                ? "answered"
                                : "unresolved",
                          },
                        }))
                      }
                    />
                  }
                  label="Student did not answer this question"
                />
                {value.reviewState === "unanswered" && (
                  <Alert severity="info">
                    Selected pages will be retained as evidence and will not be
                    extracted or marked.
                  </Alert>
                )}
                {value.reviewState === "detected" && (
                  <Alert
                    severity="warning"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        disabled={!selectedPages.length}
                        onClick={() =>
                          setQuestionValues((current) => ({
                            ...current,
                            [question.id]: {
                              ...current[question.id],
                              reviewState: "answered",
                            },
                          }))
                        }
                      >
                        Use selected pages as answer
                      </Button>
                    }
                  >
                    No answer text was detected. Confirm that the student did
                    not answer this question, or use the selected pages as an
                    answer.
                  </Alert>
                )}
                {overlaps.length > 0 && (
                  <Alert severity="info">
                    Page{overlaps.length > 1 ? "s" : ""} {overlaps.join(", ")}{" "}
                    {overlaps.length > 1 ? "are" : "is"} also assigned to
                    another question. This is allowed.
                  </Alert>
                )}
                {mappedItem &&
                  !mappedItem.pages?.length &&
                  value.reviewState === "answered" && (
                    <Alert severity="warning">
                      Original page numbers were not stored for this existing
                      mapping. Select source pages or confirm that the student
                      did not answer this question.
                    </Alert>
                  )}
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};
